import {
  DEFAULT_IMAGE_PLACEHOLDER,
  IMAGE_FALLBACK_APPLIED_ATTR,
} from '../constants/image-fallback.constants';

function isAlreadyFallback(img: HTMLImageElement, placeholderSrc: string): boolean {
  if (img.getAttribute(IMAGE_FALLBACK_APPLIED_ATTR) === '1') {
    return true;
  }
  const current = ((img.currentSrc || img.getAttribute('src')) ?? '').trim();
  if (!current) {
    return false;
  }
  return (
    current === placeholderSrc ||
    current.endsWith(placeholderSrc) ||
    current.includes('image-placeholder.svg')
  );
}

function applyPlaceholder(
  img: HTMLImageElement,
  placeholderSrc: string,
): void {
  if (isAlreadyFallback(img, placeholderSrc)) {
    img.removeAttribute('src');
    img.alt = img.alt || 'Image unavailable';
    img.style.objectFit = img.style.objectFit || 'cover';
    img.style.backgroundColor = img.style.backgroundColor || '#eef2f7';
    return;
  }

  img.setAttribute(IMAGE_FALLBACK_APPLIED_ATTR, '1');
  // Clear native onerror so a missing placeholder asset cannot recurse.
  img.onerror = null;
  img.src = placeholderSrc;
}

/** Use from template `(error)` handlers so broken URLs never recurse. */
export function handleImageLoadError(
  event: Event,
  placeholderSrc: string = DEFAULT_IMAGE_PLACEHOLDER,
): void {
  const target = event.target;
  if (!(target instanceof HTMLImageElement)) {
    return;
  }
  applyPlaceholder(target, placeholderSrc);
}

/**
 * Installs a document-level capture listener so any broken &lt;img&gt;
 * (S3, CDN, blob, etc.) is swapped to the default placeholder.
 * Safe to call once at app bootstrap.
 */
export function installGlobalImageFallback(
  doc: Document = document,
  placeholderSrc: string = DEFAULT_IMAGE_PLACEHOLDER,
): () => void {
  const onError = (event: Event): void => {
    handleImageLoadError(event, placeholderSrc);
  };

  doc.addEventListener('error', onError, true);

  return () => {
    doc.removeEventListener('error', onError, true);
  };
}
